import { GridColDef } from "@mui/x-data-grid-premium";
import { LabellerStatsEntry } from "src/store/api/generatedApi";
import { CsvColumnDef } from "src/utils/csv";
import { userDisplayName } from "src/utils/userDisplay";
import { renderLabellerTypeIcon } from "./labellerStatusFilters";

type Translate = (key: string) => string;
type FormatNumber = (value: number) => string;

export type LabellerBaseRow = LabellerStatsEntry & { id: string };

export const LABELLER_NAME_FIELD = "labeller_name";

type BuildLabellerBaseColumnsOptions = Readonly<{ showLabellerType?: boolean }>;

export const buildLabellerBaseColumns = (
  t: Translate,
  formatNumber1dp: FormatNumber,
  { showLabellerType = true }: BuildLabellerBaseColumnsOptions = {}
): GridColDef<LabellerBaseRow>[] => [
  {
    field: LABELLER_NAME_FIELD,
    headerName: t("name"),
    width: 160,
    valueGetter: (_value, row) => userDisplayName(row.labeller),
  },
  {
    field: "labeller_email",
    headerName: t("email"),
    width: 200,
    valueGetter: (_value, row) => row.labeller.email,
  },
  ...(showLabellerType
    ? [
        {
          field: "labeller_type",
          headerName: t("type"),
          width: 70,
          align: "center" as const,
          headerAlign: "center" as const,
          renderCell: ({ row }: { row: LabellerBaseRow }) =>
            renderLabellerTypeIcon(
              row.labeller.labeller_type,
              row.labeller.model_status
            ),
        },
      ]
    : []),
  {
    field: "total_time_labelled_seconds",
    headerName: t("totalTimeLabelled"),
    type: "number",
    width: 150,
    valueGetter: (_value, row) => row.total_time_labelled_seconds / 3600,
    valueFormatter: (value: number) => `${formatNumber1dp(value)}h`,
  },
  {
    field: "total_time_spent_labelling_seconds",
    headerName: t("totalTimeSpentLabelling"),
    type: "number",
    width: 180,
    valueGetter: (_value, row) =>
      row.total_time_spent_labelling_seconds != null
        ? row.total_time_spent_labelling_seconds / 3600
        : undefined,
    valueFormatter: (value?: number) =>
      value != null ? `${formatNumber1dp(value)}h` : "—",
  },
  {
    field: "label_hours_per_hour",
    headerName: t("labelHoursPerHour"),
    type: "number",
    width: 150,
    valueGetter: (_value, row) =>
      row.total_time_spent_labelling_seconds != null &&
      row.total_time_spent_labelling_seconds > 0
        ? row.total_time_labelled_seconds /
          row.total_time_spent_labelling_seconds
        : undefined,
    valueFormatter: (value?: number) =>
      value != null ? formatNumber1dp(value) : "—",
  },
  {
    field: "total_submissions",
    headerName: t("totalSubmissions"),
    type: "number",
    width: 130,
  },
  {
    field: "events_created",
    headerName: t("eventsCreated"),
    type: "number",
    width: 130,
  },
];

export const buildLabellerBaseCsvColumns = (
  t: Translate,
  { showLabellerType = true }: BuildLabellerBaseColumnsOptions = {}
): CsvColumnDef<LabellerBaseRow>[] => [
  { header: t("name"), value: (row) => userDisplayName(row.labeller) },
  { header: t("email"), value: (row) => row.labeller.email },
  ...(showLabellerType
    ? [
        {
          header: t("type"),
          value: (row: LabellerBaseRow) => row.labeller.labeller_type,
        },
      ]
    : []),
  {
    header: t("totalTimeLabelled"),
    value: (row) => row.total_time_labelled_seconds / 3600,
  },
  {
    header: t("totalTimeSpentLabelling"),
    value: (row) =>
      row.total_time_spent_labelling_seconds != null
        ? row.total_time_spent_labelling_seconds / 3600
        : null,
  },
  {
    header: t("labelHoursPerHour"),
    value: (row) =>
      row.total_time_spent_labelling_seconds != null &&
      row.total_time_spent_labelling_seconds > 0
        ? row.total_time_labelled_seconds /
          row.total_time_spent_labelling_seconds
        : null,
  },
  { header: t("totalSubmissions"), value: (row) => row.total_submissions },
  { header: t("eventsCreated"), value: (row) => row.events_created },
];
